import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownFoldMenu } from '#/components/Dropdown/menus/Fold/DropdownFoldMenu';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from '#/routing/route-constants';
import { postAListingRoute } from '#/routing/routes';

import { postOnIdealistDropdownDataByLocale } from './data/postOnIdealistDropdownDataByLocale';
import {
  PostOnIdealistDropdownDesktopText,
  PostOnIdealistDropdownMobileText,
} from './PostOnIdealistDropdown.styled';
import {
  postOnIdealistDropdownDataByResourcesItemType,
  postOnIdealistDropdownDataBySearchItemType,
} from './postOnIdealistDropdownConstants';

const triggerTextByLocale: Record<
  I18nLocale,
  { text: string; href: string; mobileText?: string }
> = {
  en: {
    mobileText: 'Post',
    text: 'Post on Idealist',
    href: postAListingRoute.with({}),
  },
  es: {
    text: 'Publica',
    href: postAListingRoute.with({}),
  },
  pt: {
    text: 'Anunciar',
    href: postAListingRoute.with({}),
  },
};

const triggerTextByLocaleUnauth: Record<
  I18nLocale,
  { text: string; href: string; mobileText?: string }
> = {
  en: {
    mobileText: 'Post',
    text: 'Post on Idealist',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.en,
  },
  es: {
    text: 'Publica',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.es,
  },
  pt: {
    text: 'Anunciar',
    href: SIGN_UP_TO_POST_URL_FOR_LOCALE.pt,
  },
};

export function PostOnIdealistDropdown() {
  const { locale } = useI18n();
  const { user } = useUser();
  const { search, resources } = postOnIdealistDropdownDataByLocale[locale];

  const { mobileText, text, href } = user
    ? triggerTextByLocale[locale]
    : triggerTextByLocaleUnauth[locale];

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  const triggerText = mobileText ? (
    <>
      <PostOnIdealistDropdownDesktopText>
        {text}
      </PostOnIdealistDropdownDesktopText>

      <PostOnIdealistDropdownMobileText>
        {mobileText}
      </PostOnIdealistDropdownMobileText>
    </>
  ) : (
    text
  );

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          variant="grey"
          qaId="page-header-post-on-idealist-dropdown-trigger"
          data={
            isDesktop
              ? {
                  type: 'link',
                  href,
                  trackClicked: {
                    name: 'Post a Listing',
                    properties: { context: 'Header' },
                  },
                }
              : { type: 'inert' }
          }
        >
          {triggerText} <DropdownChevron />
        </LayoutHeaderButton>
      }
      desktopTriggersOnHover
    >
      <DropdownFoldMenu
        left={{
          title: search.title,
          items: search.items
            .map((searchItem) => {
              const { iconName, qaId, trackClickedName } =
                postOnIdealistDropdownDataBySearchItemType[searchItem.type];

              return {
                iconName,
                title: searchItem.title,
                href: user
                  ? searchItem.href
                  : SIGN_UP_TO_POST_URL_FOR_LOCALE[locale],
                qaId,
                trackClicked: {
                  name: trackClickedName,
                  context: 'Header Dropdown > Post on Idealist',
                },
              };
            })
            .filter(Boolean),
        }}
        right={{
          title: resources.title,
          items: resources.items.map((resourceItem) => {
            const { image, imageHover, qaId, trackClickedName } =
              postOnIdealistDropdownDataByResourcesItemType[resourceItem.type];

            return {
              image,
              imageHover,
              title: resourceItem.title,
              description: resourceItem.description,
              href: resourceItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Post on Idealist',
              },
            };
          }),
        }}
      />
    </Dropdown>
  );
}
